/**
 * horn.ts — When a vehicle should sound its horn, as a pure function.
 *
 * A car ending the run without a word is the one death in this game the player
 * can't see coming: the camera looks nearly straight down, and a vehicle is on
 * you within a second of entering the frame. The horn is the warning — but only
 * if it means "I am about to hit you", so it fires on **being bearing down on**,
 * not on being nearby:
 *
 *          ╱ ─── in front, inside the radius → beep
 *   ▭▭▶  ─┤
 *          ╲ ─── in front, too far → silent
 *
 *   ▭▭▶  ·  ← already passed, however close → silent
 *
 * Tuning is `VEHICLES.HORN_RADIUS` / `HORN_CONE` in config/constants.ts.
 */
import type { Vec2 } from '../utils/vec2';

/**
 * Is `target` in the path of something at `pos` travelling at `vel`?
 *
 * @param cone the cosine of the half-angle counted as "ahead" — 1 is dead
 *             ahead only, 0 is the whole half-plane in front, and negative
 *             values would start including things behind the vehicle
 */
export function bearingDownOn(
  pos: Vec2,
  vel: Vec2,
  target: Vec2,
  radius: number,
  cone: number,
): boolean {
  const dx = target.x - pos.x;
  const dy = target.y - pos.y;
  const dist = Math.hypot(dx, dy);
  if (dist > radius) return false;
  // Sitting exactly on top of someone counts: there is no direction left to
  // check, and a horn is the least of their problems.
  if (dist === 0) return true;

  const speed = Math.hypot(vel.x, vel.y);
  if (speed === 0) return false; // parked things don't warn anyone

  // cos(angle between travel direction and the line to the target).
  return (dx * vel.x + dy * vel.y) / (dist * speed) >= cone;
}
